import {computed, reactive} from 'vue';
import {focusFirstInvalidInput} from '@/utils/pageUtils';
import useVuelidate from '@vuelidate/core';

export function useForm(initialFormData = {}, rulesFn = {}) {

    const formData = reactive(initialFormData);
    const state = reactive({
        data: formData,
        loading: false,
        loadingMessage: 'Loading...',
        success: false,
        error: false,
        errorMessage: '',
        $v: null, // We'll initialize below
    });

    const rules = computed(() => {
        return typeof rulesFn === 'function' ? rulesFn(formData) : rulesFn;
    });

    state.$v = useVuelidate(rules, formData, { $lazy: true, $autoDirty: true });

    const setLoadingState = (message = 'Loading...') => {
        state.loading = true;
        state.loadingMessage = message;
        state.error = false;
        state.errorMessage = '';
        state.success = false;
    };

    const hideLoadingState = () => {
        state.loading = false;
    };

    const setErrorState = (message) => {
        state.error = true;
        state.errorMessage = message;
    };

    const save = async (saveFunction, message = 'Saving...') => {
        setLoadingState(message);
        try {
            const result = await saveFunction();
            state.success = true;
            return result;
        } catch (error) {
            setErrorState(error?.message || 'An unexpected error occurred.');
        } finally {
            hideLoadingState();
        }
    };

    const fetch = async (fetchFunction) => {
        state.loading = true;
        state.success = false;
        state.error = false;
        try {
            await fetchFunction();
            state.success = true;
        } catch (error) {
            setErrorState(error?.message || 'An unexpected error occurred.');
        } finally {
            hideLoadingState();
        }
    };

    const focusInvalidInput = () => {
        focusFirstInvalidInput();
    };

    const scrollIntoView = (elementName) => {
        const element = document.getElementById(elementName);
        if (element) {
            element.scrollIntoView({behavior: 'smooth'});
        }
    };

    const scrollToTop = () => {
        if (typeof window !== 'undefined') {
            window.scrollTo({top: 0, behavior: 'smooth'});
        }
    };

    const setFocus = (elementName) => {
        const element = document.getElementById(elementName);
        if (element) {
            element.focus();
            if (element.select) {
                element.select();
            }
        }
    };

    const isSavable = () => {
        if (!state.$v.$anyDirty) return false;
        state.$v.$touch();
        return !state.$v.$error;
    };

    // Shallow reset
    const resetForm = () => {
        Object.assign(formData, initialFormData);
        state.$v.$reset();
        state.success = false;
        state.error = false;
    };

    return {
        state,
        $v: state.$v,
        save,
        fetch,
        setLoadingState,
        hideLoadingState,
        setErrorState,
        focusInvalidInput,
        scrollIntoView,
        scrollToTop,
        setFocus,
        isSavable,
        resetForm,
    };
}